<script>
import { h } from 'vue';
import SbKeyValue from '@src/components/KeyValue/SbKeyValue.vue';
import SbKeyValueContainer from '@src/components/KeyValue/components/SbKeyValueContainer.vue';

const keyValueContainerRenderer = (h, { emphasis, emphasisType, type }, children) => {
  return h(
    SbKeyValueContainer,
    { props: { emphasis, emphasisType, type } },
    children,
  );
};

export default {
  name: 'SbKeyValueGroup',
  provide() {
    return {
      emphasizer: {
        emphasis: this.emphasis,
        emphasisType: this.emphasisType,
      },
    };
  },
  props: {
    /**
    * Input data to iterate upon
    */
    pairs: {
      type: Array,
      default: () => ([]),
    },
    /**
    * Whether to emphasise on key or value of the pair
    */
    emphasis: {
      type: String,
      default: 'key',
      validator: (val) => val.match(/(key|value)/),
    },
    /**
    * Whether to emphasise via color or a bold font
    */
    emphasisType: {
      type: String,
      default: 'color',
      validator: (val) => val.match(/(color|bold)/),
    },
    /**
    * Position of the key-value group relative of the parent element
    */
    displayType: {
      type: String,
      default: 'inline',
      validator: (val) => val.match(/(inline|grid|center)/),
    },
  },
  methods: {
    determineRenderType() {
      if (this.$slots.default() !== undefined) return 'default-slot';
      return this.displayType;
    },
    validateSlots() {
      if (this.$slots.key() === undefined || this.$slots.value() === undefined) {
        throw new Error('slot: <key | value> is not defined.');
      }
    },
  },
  render() {
    switch (this.determineRenderType()) {
      case 'inline':
        this.validateSlots();
        return h(
          'div',
          {
            attrs: {
              class: 'sb-key-value-group sb-key-value-group__inline',
            },
          },
          this.pairs.map((pair) => h(
            SbKeyValue,
            {
              props: {
                emphasis: this.emphasis,
                emphasisType: this.emphasisType,
              },
            },
            [
              h('template', { slot: 'key' }, this.$slots.key({ pair })),
              h('template', { slot: 'value' }, this.$slots.value({ pair })),
            ],
          )),
        );
      case 'grid':
        this.validateSlots();
        return h(
          'div',
          {
            attrs: {
              class: 'sb-key-value-group sb-key-value-group__grid',
            },
          },

          this.pairs.map((pair) => {
            const options = {
              emphasis: this.emphasis,
              emphasisType: this.emphasisType,
            };
            return [
              keyValueContainerRenderer(h, { ...options, type: 'key' }, this.$slots.key({ pair })),
              keyValueContainerRenderer(h, { ...options, type: 'value' }, this.$slots.value({ pair })),
            ];
          }),
        );
      case 'center':
        this.validateSlots();
        return h(
          'div',
          {
            attrs: {
              class: 'sb-key-value-group sb-key-value-group__center',
            },
          },
          this.pairs.map((pair) => h(
            SbKeyValue,
            {
              props: {
                emphasis: this.emphasis,
                emphasisType: this.emphasisType,
              },
              class: ['sb-key-value--center'],
            },
            [
              h('template', { slot: 'key' }, this.$slots.key({ pair })),
              h('template', { slot: 'value' }, this.$slots.value({ pair })),
            ],
          )),
        );
      default: // 'default-slot'
        return h(
          'div',
          {
            attrs: {
              class: 'sb-key-value-group',
            },
          },
          this.pairs.map((pair) => this.$slots.default({ pair })),
        );
    }
  },
};
</script>

<style lang='scss'>
@import '@src/components/KeyValue/style/sb-key-value-group.scss';
</style>
